/**
 * « Mes dossiers » du technicien : kanban de ses étapes, piloté par le
 * rendez-vous affecté. Le dossier est le point d'entrée de toutes ses actions :
 * accepter / refuser (avec motif), réaliser / non effectué, puis DÉPOSER son
 * compte rendu (PDF en drag & drop + note WYSIWYG optionnelle). Tant que le
 * rapport n'est pas déposé, la mission reste « En attente du rapport » ; une
 * fois déposé, elle passe « Terminé ».
 */
import { REPORT_FILE_MAX_BYTES } from '@bcl/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Link from '@tiptap/extension-link';
import StarterKit from '@tiptap/starter-kit';
import { EditorContent, useEditor } from '@tiptap/react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import {
  listTechnicianReports,
  requestReportUploadUrl,
  submitTechnicianReport,
} from '@/features/admin/admin-api';
import {
  completeAppointment,
  listAssignedAppointments,
  respondAppointment,
  type AssignedAppointment,
} from '@/features/appointments/appointments-api';
import { formatSlot } from '@/features/appointments/appointment-labels';
import { uploadToPresignedUrl } from '@/features/documents/documents-api';
import { extractErrorMessage } from '@/features/auth/error-message';

type ColumnKey =
  | 'a_repondre'
  | 'a_realiser'
  | 'attente_rapport'
  | 'termine'
  | 'non_effectue';

const COLUMNS: { key: ColumnKey; label: string; cls: string }[] = [
  { key: 'a_repondre', label: 'À répondre', cls: 'bg-amber-100 text-amber-800' },
  { key: 'a_realiser', label: 'À réaliser', cls: 'bg-emerald-100 text-emerald-800' },
  {
    key: 'attente_rapport',
    label: 'En attente du rapport',
    cls: 'bg-sky-100 text-sky-800',
  },
  { key: 'termine', label: 'Terminé', cls: 'bg-green-100 text-green-800' },
  { key: 'non_effectue', label: 'Non effectué', cls: 'bg-red-100 text-red-800' },
];

/** Colonne d'un RDV selon son statut + l'existence d'un compte rendu. */
function columnOf(a: AssignedAppointment, hasReport: boolean): ColumnKey | null {
  switch (a.status) {
    case 'attribue':
      return 'a_repondre';
    case 'accepte':
      return 'a_realiser';
    case 'realise':
      return hasReport ? 'termine' : 'attente_rapport';
    case 'non_effectue':
      return 'non_effectue';
    default:
      return null;
  }
}

/** Éditeur WYSIWYG (TipTap) pour la note de compte rendu — HTML assaini côté serveur. */
function NoteEditor({ onChange }: { onChange: (html: string) => void }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false, autolink: true }),
    ],
    content: '',
    onUpdate: ({ editor }) => {
      onChange(editor.getText().trim() ? editor.getHTML() : '');
    },
    editorProps: {
      attributes: {
        class:
          'min-h-28 max-h-64 overflow-y-auto px-3 py-2 text-sm focus:outline-none [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5 [&_h3]:font-semibold',
      },
    },
  });

  const btn = (active: boolean) =>
    `rounded px-2 py-1 text-xs font-medium ${active ? 'bg-bcl-blue text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`;

  if (!editor) return null;
  return (
    <div className="rounded-md border">
      <div className="flex flex-wrap gap-1 border-b bg-gray-50 p-1.5">
        <button
          type="button"
          className={btn(editor.isActive('bold'))}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          Gras
        </button>
        <button
          type="button"
          className={btn(editor.isActive('italic'))}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          Italique
        </button>
        <button
          type="button"
          className={btn(editor.isActive('bulletList'))}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          Liste
        </button>
        <button
          type="button"
          className={btn(editor.isActive('orderedList'))}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          Liste num.
        </button>
        <button
          type="button"
          className={btn(editor.isActive('heading', { level: 3 }))}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
        >
          Titre
        </button>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

export function TechnicianProjectsPage() {
  const qc = useQueryClient();
  const assigned = useQuery({
    queryKey: ['technician', 'assigned'],
    queryFn: listAssignedAppointments,
  });
  const reports = useQuery({
    queryKey: ['technician', 'reports'],
    queryFn: listTechnicianReports,
  });

  const [reason, setReason] = useState<Record<string, string>>({});
  const [reportFor, setReportFor] = useState<AssignedAppointment | null>(null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['technician', 'assigned'] });
    qc.invalidateQueries({ queryKey: ['technician', 'reports'] });
  };

  const respondMut = useMutation({
    mutationFn: ({
      id,
      decision,
      reasonText,
    }: {
      id: string;
      decision: 'accepte' | 'refuse';
      reasonText?: string;
    }) => respondAppointment(id, { decision, reason: reasonText }),
    onSuccess: () => {
      toast.success('Réponse enregistrée');
      invalidate();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const completeMut = useMutation({
    mutationFn: ({
      id,
      decision,
      reasonText,
    }: {
      id: string;
      decision: 'realise' | 'non_effectue';
      reasonText?: string;
    }) => completeAppointment(id, { decision, reason: reasonText }),
    onSuccess: () => {
      toast.success('Intervention mise à jour');
      invalidate();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const items = assigned.data ?? [];
  const projectsWithReport = new Set(
    (reports.data ?? []).map((r) => r.projectId),
  );

  return (
    <DashboardLayout variant="technicien" title="Mes dossiers">
      <Card>
        <CardHeader>
          <CardTitle>Mes dossiers</CardTitle>
          <CardDescription>
            Vos dossiers par étape. Acceptez ou refusez le rendez-vous, réalisez
            l'intervention, puis déposez votre compte rendu (PDF) pour terminer.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 overflow-x-auto pb-3">
            {COLUMNS.map((col) => {
              const cards = items.filter(
                (a) =>
                  columnOf(a, projectsWithReport.has(a.projectId)) === col.key,
              );
              return (
                <div key={col.key} className="w-72 shrink-0">
                  <div className="mb-2 flex items-center justify-between">
                    <Badge variant="outline" className={col.cls}>
                      {col.label}
                    </Badge>
                    <span className="text-xs text-gray-500">{cards.length}</span>
                  </div>
                  <div className="space-y-2">
                    {cards.map((a) => (
                      <div
                        key={a.id}
                        className="space-y-2 rounded-md border bg-white p-3 text-sm"
                      >
                        <div className="font-semibold">
                          {a.fiche
                            ? `${a.fiche.client.firstName} ${a.fiche.client.lastName}`
                            : 'Client (visible après acceptation)'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {a.projectReference ?? '—'}
                        </div>
                        {a.confirmedSlot && (
                          <div className="text-xs text-gray-600">
                            📅 {formatSlot(a.confirmedSlot)}
                          </div>
                        )}
                        {a.fiche && (
                          <div className="text-xs text-gray-500">
                            {a.fiche.client.phone} · {a.fiche.client.email}
                          </div>
                        )}

                        {col.key === 'a_repondre' && (
                          <div className="space-y-2">
                            <Button
                              size="sm"
                              className="w-full"
                              disabled={respondMut.isPending}
                              onClick={() =>
                                respondMut.mutate({ id: a.id, decision: 'accepte' })
                              }
                            >
                              Accepter le rendez-vous
                            </Button>
                            <Input
                              aria-label={`Motif refus ${a.id}`}
                              placeholder="Motif (obligatoire pour refuser)"
                              value={reason[a.id] ?? ''}
                              onChange={(e) =>
                                setReason((p) => ({ ...p, [a.id]: e.target.value }))
                              }
                              className="h-8"
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              className="w-full bg-red-50 text-red-800"
                              disabled={respondMut.isPending || !reason[a.id]}
                              onClick={() =>
                                respondMut.mutate({
                                  id: a.id,
                                  decision: 'refuse',
                                  reasonText: reason[a.id],
                                })
                              }
                            >
                              Refuser
                            </Button>
                          </div>
                        )}

                        {col.key === 'a_realiser' && (
                          <div className="space-y-2">
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                className="flex-1"
                                disabled={completeMut.isPending}
                                onClick={() =>
                                  completeMut.mutate({ id: a.id, decision: 'realise' })
                                }
                              >
                                Réalisé
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="flex-1 bg-red-50 text-red-800"
                                disabled={completeMut.isPending || !reason[a.id]}
                                onClick={() =>
                                  completeMut.mutate({
                                    id: a.id,
                                    decision: 'non_effectue',
                                    reasonText: reason[a.id],
                                  })
                                }
                              >
                                Non effectué
                              </Button>
                            </div>
                            <Input
                              aria-label={`Motif non effectué ${a.id}`}
                              placeholder="Motif si non effectué"
                              value={reason[a.id] ?? ''}
                              onChange={(e) =>
                                setReason((p) => ({ ...p, [a.id]: e.target.value }))
                              }
                              className="h-8"
                            />
                          </div>
                        )}

                        {col.key === 'attente_rapport' && (
                          <Button
                            size="sm"
                            className="w-full"
                            onClick={() => setReportFor(a)}
                          >
                            Déposer le compte rendu
                          </Button>
                        )}

                        {col.key === 'termine' && (
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-medium text-green-700">
                              ✓ Compte rendu déposé
                            </span>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setReportFor(a)}
                            >
                              Ajouter
                            </Button>
                          </div>
                        )}

                        {col.key === 'non_effectue' && (
                          <span className="text-xs text-red-700">
                            Intervention non effectuée
                          </span>
                        )}
                      </div>
                    ))}
                    {cards.length === 0 && (
                      <p className="rounded-md border border-dashed p-3 text-center text-xs text-gray-300">
                        —
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {reportFor && (
        <ReportDialog
          appointment={reportFor}
          onClose={() => setReportFor(null)}
          onDone={() => {
            setReportFor(null);
            invalidate();
          }}
        />
      )}
    </DashboardLayout>
  );
}

/** Dialogue de dépôt de compte rendu : grosse drop zone PDF + note WYSIWYG. */
function ReportDialog({
  appointment,
  onClose,
  onDone,
}: {
  appointment: AssignedAppointment;
  onClose: () => void;
  onDone: () => void;
}) {
  const [title, setTitle] = useState("Compte rendu d'intervention");
  const [noteHtml, setNoteHtml] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [uploadedKey, setUploadedKey] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function handleFile(picked: File | undefined) {
    if (!picked) return;
    if (picked.type !== 'application/pdf') {
      toast.error('Le compte rendu doit être un PDF.');
      return;
    }
    if (picked.size > REPORT_FILE_MAX_BYTES) {
      toast.error('Fichier trop volumineux (max 25 Mo).');
      return;
    }
    setFile(picked);
    setUploadedKey(null);
    setProgress(0);
    try {
      const { fileS3Key, uploadUrl } = await requestReportUploadUrl(
        appointment.projectId,
        {
          filename: picked.name,
          contentType: 'application/pdf',
          sizeBytes: picked.size,
        },
      );
      await uploadToPresignedUrl(uploadUrl, picked, setProgress);
      setUploadedKey(fileS3Key);
      setProgress(100);
      toast.success('PDF téléversé.');
    } catch (e) {
      setProgress(null);
      setFile(null);
      toast.error(extractErrorMessage(e));
    }
  }

  const submitMut = useMutation({
    mutationFn: () =>
      submitTechnicianReport(appointment.projectId, {
        title: title.trim() || "Compte rendu d'intervention",
        noteHtml: noteHtml || undefined,
        fileS3Key: uploadedKey!,
        fileName: file!.name,
        fileContentType: 'application/pdf',
        fileSizeBytes: file!.size,
      }),
    onSuccess: () => {
      toast.success('Compte rendu déposé');
      onDone();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const uploading = progress !== null && progress < 100;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Compte rendu d'intervention</DialogTitle>
          <DialogDescription>
            Dossier {appointment.projectReference ?? ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Grosse drop zone PDF */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => inputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              handleFile(e.dataTransfer.files?.[0]);
            }}
            className={`flex min-h-44 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 text-center transition ${
              dragging
                ? 'border-bcl-blue bg-bcl-blue/5'
                : uploadedKey
                  ? 'border-green-400 bg-green-50'
                  : 'border-gray-300 hover:border-bcl-blue hover:bg-gray-50'
            }`}
          >
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            {uploadedKey ? (
              <>
                <span className="text-3xl">📄</span>
                <p className="mt-2 text-sm font-medium text-green-800">
                  {file?.name}
                </p>
                <p className="text-xs text-green-700">
                  PDF prêt — cliquez pour remplacer
                </p>
              </>
            ) : (
              <>
                <span className="text-4xl">⬆️</span>
                <p className="mt-2 text-sm font-medium">
                  Glissez-déposez le rapport PDF ici
                </p>
                <p className="text-xs text-gray-500">
                  ou cliquez pour parcourir · PDF uniquement · 25 Mo max
                </p>
              </>
            )}
          </div>

          {progress !== null && progress < 100 && (
            <div className="space-y-1">
              <Progress value={progress} />
              <p className="text-xs text-gray-500">Téléversement… {progress}%</p>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="cr-title">Titre</Label>
            <Input
              id="cr-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Note (optionnelle)</Label>
            <NoteEditor onChange={setNoteHtml} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button
            disabled={!uploadedKey || uploading || submitMut.isPending}
            onClick={() => submitMut.mutate()}
          >
            {submitMut.isPending ? 'Envoi…' : 'Déposer le compte rendu'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
